/**
 * Administration > Tenants (M2 W5b). Lists registry tenants and creates/edits them.
 * Create takes slug + display name (+ optional domain); edit changes the display name
 * and status. Mutations refetch the list (no optimistic state). Non-PHI registry data.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil } from "lucide-react";
import {
  SkyButton,
  SkyDataGrid,
  SkyDialog,
  SkyDropdown,
  SkyInput,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import { ApiProblem } from "../../lib/api";
import {
  listTenants,
  createTenant,
  updateTenant,
  type AdminTenant,
} from "../../lib/adminApi";
import { usePermission } from "../../lib/session";
import { AdminPage, AdminError, StatusBadge } from "./adminShared";

const STATUS_OPTIONS = [
  { value: "active", text: "Active" },
  { value: "inactive", text: "Inactive" },
  { value: "disabled", text: "Disabled" },
];

function problemTitle(e: unknown, fallback: string): string {
  return e instanceof ApiProblem ? e.problem.title : fallback;
}

export function AdminTenants() {
  const { canManage } = usePermission();
  const canEdit = canManage("Tenants");

  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<AdminTenant | null>(null);

  // Generation counter so a post-create reload cannot be overwritten by a slower mount load
  // (which would make the new tenant vanish until a manual refresh): each load is stamped and
  // only the newest one applies its result.
  const loadGenRef = useRef(0);

  const load = useCallback(() => {
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    listTenants()
      .then(({ data }) => {
        if (gen !== loadGenRef.current) return;
        setTenants(data);
      })
      .catch((e: unknown) => {
        if (gen !== loadGenRef.current) return;
        setError(problemTitle(e, "Failed to load tenants."));
        setTenants([]);
      })
      .finally(() => {
        if (gen === loadGenRef.current) setLoading(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const columns: SkyDataGridColumn<AdminTenant>[] = [
    { key: "displayName", header: "Name", width: "26%" },
    { key: "slug", header: "Slug", width: "20%" },
    {
      key: "domain",
      header: "Domain",
      width: "18%",
      renderCell: (_v, row) => row.domain ?? "",
    },
    {
      key: "status",
      header: "Status",
      width: "16%",
      renderCell: (_v, row) => <StatusBadge status={row.status} />,
    },
    {
      key: "actions",
      header: "Actions",
      width: "20%",
      renderCell: (_v, row) => (
        <SkyButton
          appearance="outline"
          size="small"
          icon={Pencil}
          disabled={!canEdit}
          onClick={() => setEditing(row)}
        >
          Edit
        </SkyButton>
      ),
    },
  ];

  return (
    <AdminPage
      crumbs={[
        { key: "home", label: "skyCare", to: "/" },
        { key: "admin", label: "Administration" },
        { key: "tenants", label: "Tenants" },
      ]}
      title="Tenants"
      description="Registry tenants that own instances and user role assignments."
      requires={["manageTenants"]}
      headerAction={
        canEdit ? (
          <SkyButton appearance="primary" icon={Plus} onClick={() => setCreateOpen(true)}>
            New tenant
          </SkyButton>
        ) : undefined
      }
    >
      {error && <AdminError title="Could not complete the request" message={error} />}

      <SkyDataGrid<AdminTenant>
        data={tenants}
        columns={columns}
        getRowId={(r) => r.id}
        loading={loading}
        emptyMessage="No tenants found"
      />

      {createOpen && (
        <CreateTenantDialog
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            load();
          }}
        />
      )}
      {editing && (
        <EditTenantDialog
          tenant={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </AdminPage>
  );
}

function CreateTenantDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid = /^[a-z0-9][a-z0-9-]*$/i.test(slug) && displayName.trim().length > 0;

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await createTenant({
        slug: slug.trim(),
        displayName: displayName.trim(),
        domain: domain.trim() || null,
      });
      onCreated();
    } catch (e: unknown) {
      setErr(problemTitle(e, "Failed to create tenant."));
      setBusy(false);
    }
  }

  return (
    <SkyDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Create tenant"
      size="md"
      primaryAction={{ label: "Create tenant", onClick: submit, loading: busy, disabled: !valid || busy }}
      secondaryAction={{ label: "Cancel", onClick: onClose, disabled: busy }}
    >
      <div className="flex flex-col gap-3">
        {err && <AdminError title="Could not create tenant" message={err} />}
        <SkyInput
          label="Slug"
          required
          placeholder="acme-health"
          hint="Lowercase letters, numbers, and hyphens."
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />
        <SkyInput
          label="Display name"
          required
          placeholder="Acme Health"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <SkyInput
          label="Domain"
          placeholder="Optional"
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
        />
      </div>
    </SkyDialog>
  );
}

function EditTenantDialog({
  tenant,
  onClose,
  onSaved,
}: {
  tenant: AdminTenant;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [displayName, setDisplayName] = useState(tenant.displayName);
  const [status, setStatus] = useState(tenant.status);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    try {
      await updateTenant(tenant.id, {
        displayName: displayName.trim(),
        status: status as "active" | "inactive" | "disabled",
      });
      onSaved();
    } catch (e: unknown) {
      setErr(problemTitle(e, "Failed to update tenant."));
      setBusy(false);
    }
  }

  return (
    <SkyDialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Edit ${tenant.slug}`}
      size="md"
      primaryAction={{ label: "Save changes", onClick: submit, loading: busy, disabled: busy }}
      secondaryAction={{ label: "Cancel", onClick: onClose, disabled: busy }}
    >
      <div className="flex flex-col gap-3">
        {err && <AdminError title="Could not update tenant" message={err} />}
        <SkyInput
          label="Display name"
          required
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
        />
        <SkyDropdown
          label="Status"
          options={STATUS_OPTIONS}
          selectedOptions={[status]}
          onOptionSelect={(vals) => setStatus(vals[0] ?? "active")}
        />
      </div>
    </SkyDialog>
  );
}
